import { z } from "zod";

import { PromptJsonSchema } from "../../../schemas/Agent/promptJson.schema";
import { IsoDateTimeParamSchema } from "../../../shared/core/iso-date-time.schema";
import { ApiSuccessSchema } from "../../common/response.schema";
import {
  VersionDetailSchema,
  VersionListResponseSchema,
  VersionMetadataSchema,
  VersionStatusSchema
} from "./schemas/version-metadata.schema";

/**
 * ZAgentVersions — Zod Validation Schemas for Agent Prompt Versioning API
 *
 * All routes are nested under /api/agents/:agentId/versions
 */
export const ZAgentVersions = {
  /** GET /api/agents/:agentId/versions — paginated list (metadata only, no promptJson) */
  List: {
    method: "GET" as const,
    path: "/api/agents/:agentId/versions",
    PathVars: z.object({ agentId: z.string() }),
    Query: z.object({
      limit: z.coerce.number().optional(),
      offset: z.coerce.number().optional(),
      type: z.enum(["CHECKPOINT", "AUTO"]).optional(),
      // `IsoDateTimeParamSchema` rather than `z.coerce.date()`: coercion is
      // `new Date(...)` behind a schema, so `0`, `false` and `"0"` become
      // plausible instants and `"05/15/2026"` is read month-first. The caller
      // gets a well-formed filter over the wrong window instead of a 400. It
      // also renders as `{}` under `z.toJSONSchema`, so the route's documented
      // "ISO timestamp" and the schema a generator reads disagree.
      // Output stays `Date`, so the repository and the Prisma `createdAt`
      // filter below are untouched.
      createdBefore: IsoDateTimeParamSchema.optional(),
      createdAfter: IsoDateTimeParamSchema.optional()
    }),
    Response: ApiSuccessSchema(VersionListResponseSchema)
  },

  /** GET /api/agents/:agentId/versions/status — lightweight status for editor header */
  Status: {
    method: "GET" as const,
    path: "/api/agents/:agentId/versions/status",
    PathVars: z.object({ agentId: z.string() }),
    Response: ApiSuccessSchema(VersionStatusSchema)
  },

  /** GET /api/agents/:agentId/versions/:versionId — full version with promptJson */
  Get: {
    method: "GET" as const,
    path: "/api/agents/:agentId/versions/:versionId",
    PathVars: z.object({ agentId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(VersionDetailSchema)
  },

  /** POST /api/agents/:agentId/versions — create a manual checkpoint */
  Create: {
    method: "POST" as const,
    path: "/api/agents/:agentId/versions",
    PathVars: z.object({ agentId: z.string() }),
    Body: z.object({
      promptJson: PromptJsonSchema,
      name: z.string().max(100).optional(),
      description: z.string().max(500).optional()
    }),
    Response: ApiSuccessSchema(VersionMetadataSchema)
  },

  /** POST /api/agents/:agentId/versions/auto — auto-save (type=AUTO, minimal response) */
  AutoSave: {
    method: "POST" as const,
    path: "/api/agents/:agentId/versions/auto",
    PathVars: z.object({ agentId: z.string() }),
    Body: z.object({
      promptJson: PromptJsonSchema
    }),
    Response: ApiSuccessSchema(z.object({ id: z.string(), createdAt: z.coerce.date() }))
  },

  /** PATCH /api/agents/:agentId/versions/:versionId — update name/description */
  Update: {
    method: "PATCH" as const,
    path: "/api/agents/:agentId/versions/:versionId",
    PathVars: z.object({ agentId: z.string(), versionId: z.string() }),
    Body: z.object({
      name: z.string().max(100).optional(),
      description: z.string().max(500).optional()
    }),
    Response: ApiSuccessSchema(VersionMetadataSchema)
  },

  /** POST /api/agents/:agentId/versions/:versionId/restore — restore version to editor */
  Restore: {
    method: "POST" as const,
    path: "/api/agents/:agentId/versions/:versionId/restore",
    PathVars: z.object({ agentId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ promptJson: PromptJsonSchema, message: z.string() }))
  },

  /** POST /api/agents/:agentId/versions/:versionId/publish — set version as production */
  Publish: {
    method: "POST" as const,
    path: "/api/agents/:agentId/versions/:versionId/publish",
    PathVars: z.object({ agentId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(VersionDetailSchema)
  },

  /** POST /api/agents/:agentId/versions/publish-draft — set current draft as production */
  PublishDraft: {
    method: "POST" as const,
    path: "/api/agents/:agentId/versions/publish-draft",
    PathVars: z.object({ agentId: z.string() }),
    Response: ApiSuccessSchema(z.object({ message: z.string() }))
  },

  /** DELETE /api/agents/:agentId/versions/:versionId — delete a checkpoint version */
  Delete: {
    method: "DELETE" as const,
    path: "/api/agents/:agentId/versions/:versionId",
    PathVars: z.object({ agentId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ id: z.string(), deleted: z.boolean() }))
  }
} as const;
